
import { useEffect, useMemo, useState } from "react";
import { FiDownload, FiEye, FiSearch, FiShoppingBag } from "react-icons/fi";
import api from "../services/api";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const Members = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");
  const [updatedAtFrom, setUpdatedAtFrom] = useState("");
  const [updatedAtTo, setUpdatedAtTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [exportError, setExportError] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState("");
  const [isIdRefreshing, setIsIdRefreshing] = useState(false);
  const [idRefreshError, setIdRefreshError] = useState("");
  const [isPurchasesOpen, setIsPurchasesOpen] = useState(false);
  const [purchasesRows, setPurchasesRows] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchasesError, setPurchasesError] = useState("");

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchMembers = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/members", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            status_id: statusFilter || undefined,
            created_at_from: createdAtFrom || undefined,
            created_at_to: createdAtTo || undefined,
            updated_at_from: updatedAtFrom || undefined,
            updated_at_to: updatedAtTo || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load members.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
        setStatusOptions(data.status_options || []);
      } catch (error) {
        setLoadError("Failed to load members.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, [
    query,
    statusFilter,
    createdAtFrom,
    createdAtTo,
    updatedAtFrom,
    updatedAtTo,
    sortBy,
    sortDir,
    page,
    perPage,
  ]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const handleExportExcel = async () => {
    if (!createdAtFrom || !createdAtTo) {
      setExportError("Created At From and Created At To are required for export.");
      return;
    }

    setExportError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setExportError("Missing access token.");
        return;
      }

      const response = await api.get("/members/export", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: query,
          status_id: statusFilter || undefined,
          created_at_from: createdAtFrom,
          created_at_to: createdAtTo,
          updated_at_from: updatedAtFrom || undefined,
          updated_at_to: updatedAtTo || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.ms-excel",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `members-export-${createdAtFrom}-to-${createdAtTo}.csv`;
      link.href = url;
      link.download = match?.[1] || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError("Failed to export members.");
    }
  };

  const handleView = async (id) => {
    setIsViewOpen(true);
    setViewError("");
    setViewData(null);
    setIdRefreshError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setViewError("Missing access token.");
        return;
      }

      const response = await api.get(`/members/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setViewError("Failed to load member details.");
        return;
      }

      setViewData(response.data?.data || null);
    } catch (error) {
      setViewError("Failed to load member details.");
    }
  };

  const handleRefreshIdUpload = async () => {
    if (!viewData?.member?.oauth_user_id || !viewData?.id_upload?.upload_id) {
      return;
    }

    setIsIdRefreshing(true);
    setIdRefreshError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setIdRefreshError("Missing access token.");
        return;
      }

      const response = await api.post(
        `/members/${viewData.member.oauth_user_id}/id-upload/refresh`,
        { upload_id: viewData.id_upload.upload_id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status !== "success") {
        setIdRefreshError("Failed to refresh signed URL.");
        return;
      }

      setViewData((prev) => ({
        ...prev,
        id_upload: {
          ...prev?.id_upload,
          signed_url: response.data?.data?.signed_url || prev?.id_upload?.signed_url,
          url_expires_at: response.data?.data?.url_expires_at || prev?.id_upload?.url_expires_at,
        },
      }));
    } catch (error) {
      setIdRefreshError("Failed to refresh signed URL.");
    } finally {
      setIsIdRefreshing(false);
    }
  };

  const handleOpenPurchases = async (id) => {
    setIsPurchasesOpen(true);
    setPurchasesRows([]);
    setPurchasesError("");
    setPurchasesLoading(true);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setPurchasesError("Missing access token.");
        return;
      }

      const response = await api.get(`/members/${id}/purchases`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setPurchasesError("Failed to load purchases.");
        return;
      }

      setPurchasesRows(response.data?.data?.rows || []);
    } catch (error) {
      setPurchasesError("Failed to load purchases.");
    } finally {
      setPurchasesLoading(false);
    }
  };
  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatBytes = (value) => {
    if (!value && value !== 0) {
      return "-";
    }
    const bytes = Number(value);
    if (Number.isNaN(bytes)) {
      return "-";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const isUrlExpired = (expiresAt) => {
    if (!expiresAt) {
      return true;
    }
    const expiry = new Date(expiresAt);
    if (Number.isNaN(expiry.getTime())) {
      return true;
    }
    return expiry.getTime() <= Date.now();
  };

  const getAccountStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "pre registered") {
      return "bg-slate-100 text-slate-600";
    }
    if (normalized === "for validation") {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized === "for activation") {
      return "bg-blue-100 text-blue-700";
    }
    if (normalized === "active") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "inactive") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "expired") {
      return "bg-red-100 text-red-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const getPurchaseStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "for validation") {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized === "approved") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "rejected") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "cancelled" || normalized === "canceled") {
      return "bg-red-100 text-red-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const renderPages = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  const viewMember = viewData?.member;
  const idUpload = viewData?.id_upload;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Members
        </p>
        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Members Directory</h1>
            <p className="mt-2 text-sm text-slate-500">
              Manage member profiles, account status, and purchase activity.
            </p>
          </div>
          {/* <button
            type="button"
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
          >
            <FiDownload />
            Export Summary
          </button> */}
        </div>
      </div>

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search members"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </form>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Display</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {PER_PAGE_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Status</option>
              {statusOptions.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2 lg:ml-auto">
              <button
                type="button"
                onClick={handleExportExcel}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300"
              >
                <FiDownload />
                Export Excel
              </button>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Created From</span>
              <input
                type="date"
                value={createdAtFrom}
                onChange={(event) => {
                  setCreatedAtFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Created To</span>
              <input
                type="date"
                value={createdAtTo}
                onChange={(event) => {
                  setCreatedAtTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Updated From</span>
              <input
                type="date"
                value={updatedAtFrom}
                onChange={(event) => {
                  setUpdatedAtFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Updated To</span>
              <input
                type="date"
                value={updatedAtTo}
                onChange={(event) => {
                  setUpdatedAtTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

        {exportError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {exportError}
          </div>
        )}

        {loadError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          <div className="w-full overflow-x-auto">
            <table className="min-w-[1200px] w-full text-left text-sm text-slate-600">
              <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
                <tr>
                  {[
                    { key: "username", label: "Username" },
                    { key: "last_name", label: "Last Name" },
                    { key: "first_name", label: "First Name" },
                    { key: "middle_name", label: "Middle Name" },
                    { key: "suffix", label: "Suffix" },
                    { key: "mobile_number", label: "Mobile No" },
                    { key: "email", label: "Email Address" },
                    { key: "date_of_birth", label: "Date of Birth" },
                    { key: "gender", label: "Gender" },
                    { key: "status", label: "Status" },
                  ].map((column) => (
                    <th
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      className="cursor-pointer px-4 py-3 text-xs font-semibold"
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {sortBy === column.key && (
                          <span className="text-white">{sortDir === "asc" ? "▲" : "▼"}</span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {isLoading ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={11}>
                      Loading members...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={11}>
                      No members found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.oauth_user_id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        {row.username || "-"}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        {row.last_name || "-"}
                      </td>
                      <td className="px-4 py-3">{row.first_name || "-"}</td>
                      <td className="px-4 py-3">{row.middle_name || "-"}</td>
                      <td className="px-4 py-3">{row.suffix || "-"}</td>
                      <td className="px-4 py-3">{row.mobile_number || "-"}</td>
                      <td className="px-4 py-3">{row.email || "-"}</td>
                      <td className="px-4 py-3">{formatDate(row.date_of_birth)}</td>
                      <td className="px-4 py-3">{row.gender || "-"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${getAccountStatusClass(
                            row.account_status_name
                          )}`}
                        >
                          {row.account_status_name || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleView(row.oauth_user_id)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FiEye />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenPurchases(row.oauth_user_id)}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                          >
                            <FiShoppingBag />
                            Purchases
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {rows.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPages()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isViewOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50">
          <div className="h-full w-full overflow-y-auto bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Member Profile
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Member Details
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Review profile, account, and ID attachment details.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {viewError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {viewError}
              </div>
            )}

            {!viewError && !viewMember && (
              <p className="mt-6 text-sm text-slate-400">Loading details...</p>
            )}

            {viewMember && (
              <div className="mt-6 space-y-6">
                <div className="grid gap-4 lg:grid-cols-2">
                  {[
                    { label: "Referral Code", value: viewMember.referral_code },
                    { label: "Username", value: viewMember.username },
                    { label: "Last Name", value: viewMember.last_name },
                    { label: "First Name", value: viewMember.first_name },
                    { label: "Middle Name", value: viewMember.middle_name },
                    { label: "Suffix", value: viewMember.suffix },
                    { label: "Date of Birth", value: formatDate(viewMember.date_of_birth) },
                    { label: "Gender", value: viewMember.gender },
                    { label: "Mobile No", value: viewMember.mobile_number },
                    { label: "Email Address", value: viewMember.email },
                    { label: "Account Status", value: viewMember.account_status_name },
                    { label: "ID Type", value: viewMember.id_type_name },
                    { label: "Home Address", value: viewMember.home_address },
                    { label: "Guardian Name", value: viewMember.guardian_name },
                    {
                      label: "Relationship to Principal",
                      value: viewMember.relationship_to_principal,
                    },
                    { label: "Profile Created", value: formatDateTime(viewMember.profile_created_at) },
                    { label: "Profile Updated", value: formatDateTime(viewMember.profile_updated_at) },
                    { label: "OAuth Created", value: formatDateTime(viewMember.oauth_created_at) },
                    { label: "OAuth Updated", value: formatDateTime(viewMember.oauth_updated_at) },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                    >
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        {item.label}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-700">
                        {item.value || "-"}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                        Uploaded ID
                      </p>
                      <h4 className="mt-1 text-lg font-semibold text-slate-900">
                        ID Attachment Details
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={handleRefreshIdUpload}
                      disabled={
                        !idUpload?.upload_id ||
                        isIdRefreshing ||
                        !isUrlExpired(idUpload?.url_expires_at)
                      }
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isIdRefreshing ? "Refreshing..." : "Refresh Link"}
                    </button>
                  </div>

                  <div className="mt-4 grid gap-3 text-sm text-slate-700 lg:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        File Name
                      </p>
                      <p className="mt-1 font-semibold">
                        {idUpload?.original_file_name || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        File Type
                      </p>
                      <p className="mt-1 font-semibold">{idUpload?.file_type || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        File Size
                      </p>
                      <p className="mt-1 font-semibold">{formatBytes(idUpload?.file_size)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        Status
                      </p>
                      <p className="mt-1 font-semibold">{idUpload?.status || "-"}</p>
                    </div>
                    <div className="lg:col-span-2">
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        Signed URL
                      </p>
                      <div className="mt-1 break-all text-sm font-semibold text-[var(--brand-blue)]">
                        {idUpload?.signed_url || "-"}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        URL Expires At
                      </p>
                      <p className="mt-1 font-semibold">{idUpload?.url_expires_at || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-400">
                        Link Status
                      </p>
                      <p className="mt-1 font-semibold">
                        {isUrlExpired(idUpload?.url_expires_at) ? "Expired" : "Active"}
                      </p>
                    </div>
                  </div>

                  {idUpload?.signed_url && (
                    <div className="mt-4">
                      <a
                        href={idUpload.signed_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
                      >
                        View Uploaded ID
                      </a>
                    </div>
                  )}

                  {idRefreshError && (
                    <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                      {idRefreshError}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {isPurchasesOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-4xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Purchases
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Member Purchases
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Review products and purchase activity for this member.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPurchasesOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {purchasesError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {purchasesError}
              </div>
            )}

            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
              <div className="w-full overflow-x-auto">
                <table className="min-w-[900px] w-full text-left text-sm text-slate-600">
                  <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
                    <tr>
                      <th className="px-4 py-3 text-xs font-semibold">Product</th>
                      <th className="px-4 py-3 text-xs font-semibold">Payment Date</th>
                      <th className="px-4 py-3 text-xs font-semibold">Activation Date</th>
                      <th className="px-4 py-3 text-xs font-semibold">Expiration Date</th>
                      <th className="px-4 py-3 text-xs font-semibold">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {purchasesLoading ? (
                      <tr>
                        <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={6}>
                          Loading purchases...
                        </td>
                      </tr>
                    ) : purchasesRows.length === 0 ? (
                      <tr>
                        <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={6}>
                          No purchases found.
                        </td>
                      </tr>
                    ) : (
                      purchasesRows.map((purchase) => (
                        <tr key={purchase.id} className="border-t border-slate-100">
                          <td className="px-4 py-3 font-semibold text-slate-700">
                            {purchase.title || "-"}
                            {purchase.secondary_title ? (
                              <div className="text-xs text-slate-400">
                                {purchase.secondary_title}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3">
                            {formatDateTime(purchase.payment_date)}
                          </td>
                          <td className="px-4 py-3">
                            {formatDateTime(purchase.activation_date)}
                          </td>
                          <td className="px-4 py-3">
                            {formatDateTime(purchase.expiration_date)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-semibold ${getPurchaseStatusClass(
                                purchase.purchase_status_name
                              )}`}
                            >
                              {purchase.purchase_status_name || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700">
                            PHP {Number(purchase.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsPurchasesOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
